/**
 * @fileoverview Image attachment types and limits for the composer.
 *
 * This module is one of exactly TWO declarations of the attachment allowlist —
 * the other is `backend/src/constants/attachments.js`. The two sides of the wire
 * cannot share a module (separate packages, no shared/ directory), so they are
 * pinned together by a single equality test that imports both:
 * `frontend-user/test/constants/attachments.test.js`.
 *
 * IMPORTANT: this module must stay DEPENDENCY-FREE, so that the pin test can
 * import its backend counterpart from jest's jsdom environment.
 *
 * Everything here derives from IMAGE_EXTENSIONS — the accepted type list and the
 * file-picker `accept` string are computed, never re-typed. Before this, the
 * allowlist existed in five hand-maintained places and had already drifted (the
 * camera input accepted `image/*`, wider than validation allowed).
 *
 * @module constants/attachments
 */

/**
 * Accepted wire types mapped to their canonical file extension. The single root
 * from which the accepted list and picker filter are derived.
 *
 * Matches the Anthropic Vision API's supported formats exactly.
 */
export const IMAGE_EXTENSIONS = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'image/gif': 'gif'
};

/** MIME types accepted without conversion. Derived — do not hand-maintain. */
export const ACCEPTED_IMAGE_TYPES = Object.keys(IMAGE_EXTENSIONS);

/**
 * Types converted to an accepted type at ingestion rather than sent as-is.
 *
 * Deliberately NOT added to the picker `accept` string: iOS Safari transcodes
 * HEIC to JPEG on upload when `accept` names concrete raster types, and that is
 * the platform where users most often attach photos. Widening `accept` risks
 * disabling that transcode. HEIC is instead caught at ingestion, which covers
 * the paths that bypass `accept` — paste, drag-and-drop, and desktop pickers.
 */
export const CONVERTIBLE_IMAGE_TYPES = ['image/heic', 'image/heif'];

/** File extensions used to identify convertible files that report a blank MIME type. */
export const CONVERTIBLE_EXTENSIONS = ['heic', 'heif'];

/** Maximum attachments per message. */
export const MAX_IMAGES = 4;

/** Maximum size of a single selected file, in MB. */
export const MAX_IMAGE_SIZE_MB = 10;

/** Maximum decoded size of all attachments on one message, in MB. */
export const MAX_TOTAL_IMAGE_SIZE_MB = 12;

/**
 * Maximum width or height, in pixels, before an image is downscaled.
 *
 * 1568 is the standard-tier long-edge limit for Claude vision. Token cost is
 * purely dimensional — ceil(w/28) * ceil(h/28) visual tokens — so this value,
 * not JPEG quality, is the lever that controls attachment cost.
 *
 * It is deliberately pinned to the STANDARD tier rather than the high-resolution
 * tier (2576px). High-res-tier models (Opus 4.8, Sonnet 5) do not downscale a
 * 2048px image, so the previous 2048 cap made per-image cost roughly triple the
 * moment ANTHROPIC_CHAT_MODEL was pointed at one of them. Capping at 1568 makes
 * cost identical on both tiers, and costs nothing on standard-tier models, which
 * were downscaling to this size server-side anyway.
 */
export const MAX_IMAGE_DIMENSION = 1568;

/**
 * JPEG/WebP encode quality, applied exactly once per image.
 *
 * Quality does not affect token cost at all — only payload bytes. It is kept
 * high because the dominant attachment here is a screenshot of text, and the
 * Anthropic vision guidance warns that heavy compression (especially repeated
 * passes) makes text hard to read.
 */
export const IMAGE_QUALITY = 0.85;

/**
 * Build the `accept` attribute for a file input.
 *
 * @returns {string} Comma-separated MIME list for `<input type="file">`
 */
export function pickerAcceptAttr() {
  return ACCEPTED_IMAGE_TYPES.join(',');
}

/**
 * Canonical file extension for an accepted MIME type.
 *
 * @param {string} mimeType - Image MIME type
 * @returns {string} Extension without a leading dot; falls back to `png`
 */
export function extensionForType(mimeType) {
  return IMAGE_EXTENSIONS[mimeType] || 'png';
}
